const SHOP_THE_LOOK_SELECTOR = '[data-product-shop-the-look]';
const PRODUCT_INFORMATION_SELECTOR = '[data-testid="product-information"]';
const PRODUCT_MEDIA_SELECTOR = '[data-testid="product-information-media"]';
const PENDING_ATTRIBUTE = 'data-product-shop-the-look-pending';
const PRODUCT_MEDIA_ACTIONS_STACK_SELECTOR = '[data-product-media-actions-stack]';
const observedProductInformation = new WeakSet();

function getElements(root, selector) {
  const elements = [...root.querySelectorAll(selector)];

  if (root instanceof Element && root.matches(selector)) {
    elements.unshift(root);
  }

  return elements;
}

function getProductMediaTarget(productInformation) {
  const mediaCandidates = getElements(productInformation, PRODUCT_MEDIA_SELECTOR);

  // 插件替换图库时可能保留隐藏的旧容器；始终优先挂到实际可见的主图容器。
  return mediaCandidates.find((media) => getComputedStyle(media).display !== 'none') ?? mediaCandidates[0];
}

function getProductMediaActionsStack(productMedia) {
  let stack = productMedia.querySelector(PRODUCT_MEDIA_ACTIONS_STACK_SELECTOR);

  if (!stack) {
    stack = document.createElement('div');
    stack.className = 'product-media-actions-stack';
    stack.setAttribute('data-product-media-actions-stack', '');
    productMedia.appendChild(stack);
  }

  return stack;
}

function ensureRelativePosition(element) {
  if (getComputedStyle(element).position === 'static') {
    element.style.position = 'relative';
  }
}

function mountProductShopTheLook(root = document) {
  const scope = root.querySelectorAll ? root : document;

  scope.querySelectorAll(SHOP_THE_LOOK_SELECTOR).forEach((component) => {
    const productInformation = component.closest(PRODUCT_INFORMATION_SELECTOR);
    const productMedia = productInformation && getProductMediaTarget(productInformation);

    if (!productMedia) return;

    const target = getProductMediaActionsStack(productMedia);

    ensureRelativePosition(productMedia);

    if (component.parentElement !== target) {
      target.appendChild(component);
    }

    component.removeAttribute(PENDING_ATTRIBUTE);
  });
}

function observeProductMedia(root = document) {
  getElements(root, PRODUCT_INFORMATION_SELECTOR).forEach((productInformation) => {
    if (observedProductInformation.has(productInformation)) return;

    observedProductInformation.add(productInformation);

    new MutationObserver((mutations) => {
      const hasNewMedia = mutations.some(({ addedNodes }) =>
        [...addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches(PRODUCT_MEDIA_SELECTOR) ||
              node.querySelector(PRODUCT_MEDIA_SELECTOR))
        )
      );

      if (hasNewMedia) {
        mountProductShopTheLook(productInformation);
      }
    }).observe(productInformation, { childList: true, subtree: true });
  });
}

class ProductShopTheLookList extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    this.addEventListener('mousedown', this.handleSelectMouseDown);
    this.addEventListener('click', this.handleSelectClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('mousedown', this.handleSelectMouseDown);
    this.removeEventListener('click', this.handleSelectClick);
  }

  handleSelectMouseDown = (event) => {
    const select = event.target;

    if (select instanceof HTMLSelectElement && select.matches('[data-shop-the-look-option]')) {
      event.stopPropagation();
    }
  };

  handleSelectClick = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-shop-the-look-option]')) return;

    event.stopPropagation();

    if (typeof select.showPicker !== 'function') return;

    try {
      select.focus({ preventScroll: true });
      select.showPicker();
      event.preventDefault();
    } catch {
      // 浏览器不允许显式打开时，继续交给原生 select 默认行为处理。
    }
  };

  handleChange = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-shop-the-look-option]')) return;

    const item = select.closest('[data-shop-the-look-item]');

    if (item instanceof HTMLElement) {
      this.updateItem(item);
    }
  };

  updateItem(item) {
    const variantsScript = item.querySelector('[data-shop-the-look-variants]');
    const variantInput = item.querySelector('[data-shop-the-look-variant-id]');
    const image = item.querySelector('[data-shop-the-look-image]');
    const price = item.querySelector('[data-shop-the-look-price-current]');
    const comparePrice = item.querySelector('[data-shop-the-look-price-compare]');
    const quantityInput = item.querySelector('input[name="quantity"]');
    const productForm = item.querySelector('product-form-component');
    const addToCartComponent = item.querySelector('add-to-cart-component');
    const addButton = item.querySelector('[ref="addToCartButton"]');
    const addButtonText = addButton?.querySelector('.add-to-cart-text__content span span');
    const selects = [...item.querySelectorAll('[data-shop-the-look-option]')];

    if (!variantsScript || !(variantInput instanceof HTMLInputElement)) return;

    const variants = JSON.parse(variantsScript.textContent || '[]');
    const selectedOptions = selects.map((select) => select.value);
    const selectedVariant = variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!selectedVariant) {
      variantInput.value = '';
      variantInput.disabled = true;

      if (addButton instanceof HTMLButtonElement) {
        addButton.disabled = true;
      }

      if (addButtonText) {
        addButtonText.textContent = item.dataset.unavailableText || '';
      }

      return;
    }

    variantInput.value = selectedVariant.id;
    variantInput.disabled = !selectedVariant.available;

    if (image instanceof HTMLImageElement && selectedVariant.image) {
      image.src = selectedVariant.image;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    }

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = selectedVariant.quantity || 1;
    }

    if (productForm instanceof HTMLElement) {
      productForm.dataset.quantityDefault = selectedVariant.quantity || 1;
    }

    if (addToCartComponent instanceof HTMLElement && selectedVariant.image) {
      addToCartComponent.dataset.productVariantMedia = selectedVariant.image;
    }

    if (price) {
      price.textContent = selectedVariant.price;
    }

    if (comparePrice) {
      if (selectedVariant.compareAtPrice) {
        comparePrice.textContent = selectedVariant.compareAtPrice;
        comparePrice.hidden = false;
      } else {
        comparePrice.textContent = '';
        comparePrice.hidden = true;
      }
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !selectedVariant.available;
    }

    if (addButtonText) {
      addButtonText.textContent = selectedVariant.available
        ? item.dataset.addText || ''
        : item.dataset.soldOutText || '';
    }
  }
}

if (!customElements.get('product-shop-the-look-list')) {
  customElements.define('product-shop-the-look-list', ProductShopTheLookList);
}

mountProductShopTheLook();
observeProductMedia();

// Shopify 编辑器局部刷新后，需要重新挂载到主图区域。
document.addEventListener('shopify:section:load', (event) => {
  mountProductShopTheLook(event.target);
  observeProductMedia(event.target);
});

document.addEventListener('shopify:block:select', () => {
  mountProductShopTheLook();
});
